import { View, getRelativeOffset } from '@play-co/timestep-core/ui';
import { randomRange } from '../minigame/match2-timestep/odie';
import gsap from 'gsap';
import { getApplication } from 'src/lib/utils';
import { animateScore, getBurstPool, getIconPool } from './effects';
import sounds from 'src/lib/sounds';
import { throttle } from './animation.util';

const ZINDEX = 9999;

const sfxProgress = throttle(
  () => sounds.playSound('progressAwarded', 0.5),
  100,
);

type FeatureButton = View & {
  container?: View;
  playHitAnimation?: () => void;
};

/**
 * Play the jumping icon poinst in a feature button.
 * @param args
 */
export async function animateJumpingPoints(args: {
  button: FeatureButton;
  count: number;
  iconImage: string;
  iconSize?: number;
}) {
  const { button, count } = args;

  // Skip if there is no count - gsap promise never resolves if duration is 0.
  if (count <= 0) return;

  const root = getApplication().getRootView();
  const pos = getRelativeOffset(button, root);
  const orientation = getOrientation(button);
  const center = {
    x: pos.x + button.style.width * 0.5,
    y: pos.y + button.style.height * 0.5,
  };
  const shortBurst = getBurstPool().obtainView();
  const bigBurst = getBurstPool().obtainView();

  sounds.playSound('win', 0.5);

  // Play a big burst over the button on start
  bigBurst.updateOpts({
    superview: root,
    x: center.x,
    y: center.y,
    scale: 1,
    zIndex: ZINDEX,
  });
  bigBurst.play('eventicon_burst', () => {
    bigBurst.removeFromSuperview();
    getBurstPool().releaseView(bigBurst);
  });

  // Create a container inside the button if not defined yet. That will be used for scale animation.
  if (!button.container) {
    const children = button.getSubviews();
    const container = new View({
      superview: button,
      width: button.style.width,
      height: button.style.height,
      anchorX: button.style.width * 0.5,
      anchorY: button.style.height * 0.5,
    });
    children.forEach((child) => container.addSubview(child));
    button.container = container;
  }

  animateScore({
    superview: root,
    from: {
      x: center.x + 120 * orientation,
      y: center.y,
    },
    zIndex: ZINDEX + 1,
    count: args.count,
    scale: 0.75,
  });

  // Cap the number of icons to 15 - that would seem like a lot anyway
  const numIcons = Math.min(count, 15);
  const interval = Math.min(0.2, 2 / numIcons);

  for (let i = 0; i < numIcons; i++) {
    animateJumpingIcon({
      ...args,
      root,
      center,
      delay: i * interval,
      onHit: () => {
        // Play the short burst animation in the point of impact
        shortBurst.updateOpts({
          superview: root,
          x: center.x,
          y: center.y,
          scale: 1,
          zIndex: ZINDEX,
        });
        shortBurst.play('currencycollect_impact_final', () => {
          shortBurst.removeFromSuperview();
          getBurstPool().releaseView(shortBurst);
        });
      },
    });
  }

  await gsap.to({}, { duration: Math.min(1, numIcons * interval) });
}

/**
 * Get the screen orientation based on the button position.
 * 1 is left, -1 is right.
 * @param button
 * @returns
 */
function getOrientation(button: FeatureButton) {
  const relativeOffset = getRelativeOffset(
    button,
    getApplication().getRootView(),
  );
  return relativeOffset.x < 300 ? 1 : -1;
}

/**
 * Animated a single jumping icon.
 * @param args
 */
async function animateJumpingIcon(args: {
  root: View;
  button: FeatureButton;
  center: { x: number; y: number };
  iconImage: string;
  iconSize?: number;
  delay: number;
  onHit?: () => void;
}) {
  const { root, button, center, iconImage, iconSize = 80, delay, onHit } = args;
  if (delay) await gsap.to({}, { duration: delay });

  const orientation = getOrientation(button);
  const from = { x: center.x + 120 * orientation, y: center.y };
  const to = { x: center.x, y: center.y };
  const spawnDuration = 0.3;
  const moveDuration = 0.6;
  const icon = getIconPool().obtainView();

  icon.updateOpts({
    superview: root,
    width: iconSize,
    height: iconSize,
    centerOnOrigin: true,
    scaleMethod: 'contain',
    image: iconImage,
    x: from.x + randomRange(-30, 30),
    y: from.y + randomRange(-30, 30),
    scale: 0,
    zIndex: ZINDEX,
  });

  // Create the icon
  await gsap.to(icon.style, {
    scale: 1,
    duration: spawnDuration,
    ease: 'back.out',
  });

  // Animate x and y movement towards the button
  gsap.to(icon.style, { x: to.x, duration: moveDuration, ease: 'linear' });
  await gsap.to(icon.style, {
    y: to.y - 80,
    duration: moveDuration * 0.5,
    ease: 'quad.out',
  });
  await gsap.to(icon.style, {
    y: to.y,
    duration: moveDuration * 0.5,
    ease: 'quad.in',
  });

  // Remove the icon and play the impact burst
  icon.removeFromSuperview();
  getIconPool().releaseView(icon);

  // Play the sfx
  sfxProgress();

  // Call the onHit callback, if available
  onHit?.();

  // Play the hit animation on the button, if available
  button.playHitAnimation?.();

  // Animated bounce effect on the button's container.
  if (button.container) {
    gsap.killTweensOf(button.container.style);
    await gsap.to(button.container.style, {
      scale: 1.1,
      duration: 0.1,
      ease: 'linear',
    });
    await gsap.to(button.container.style, {
      scale: 1,
      duration: 0.3,
      ease: 'back.out',
    });
  }
}
